import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import Crate from '@/pages/Crate';
import Upload from '@/pages/Upload';
import TracklistUpload from '@/pages/TracklistUpload';
import SharedView from '@/pages/SharedView';
import Album from '@/pages/Album';
import Owned from '@/pages/Owned';
import Unidentified from '@/pages/Unidentified';
import Review from '@/pages/Review';
import Profile from '@/pages/Profile';
import ProfileEdit from '@/pages/ProfileEdit';
import Stats from '@/pages/Stats';
import OAuthSuccess from '@/pages/OAuthSuccess';
import Pricing from '@/pages/Pricing';
import BillingSuccess from '@/pages/BillingSuccess';
import Promo from '@/pages/Promo';
import AppShell from '@/components/AppShell';

/**
 * Catches Firefox-strict unhandled Promise rejections from third-party APIs
 * (HTMLAudioElement.play(), MediaSession, focus traps, etc.) so they don't
 * surface as React's dev-mode runtime error overlay. We log them quietly.
 * Note: only suppresses NON-critical media/UI rejections; app errors still bubble.
 */
function useUnhandledRejectionGuard() {
  useEffect(() => {
    const handler = (event) => {
      const reason = event.reason;
      const msg = reason?.message || String(reason || '');
      const name = reason?.name || '';
      // Common Firefox media policy rejection patterns
      const isMediaError =
        name === 'NotAllowedError' ||
        name === 'AbortError' ||
        name === 'NotSupportedError' ||
        /play\(\) request was interrupted|user didn'?t interact|autoplay|paused|aborted/i.test(msg);
      if (isMediaError) {
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) {
    // Unauth users get bounced to the marketing Landing page (`/`) rather
    // than straight to /login. This makes shared PWA-icon links (which
    // start at /crate per the manifest) land first-time visitors on the
    // value-prop page so they can decide whether to sign up, instead of
    // immediately staring at a login form with no context.
    return <Navigate to="/" replace />;
  }
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    // Honor the user's chosen default home page (set via Profile toggle).
    // Defaults to '/crate' (the value page) when the field isn't set —
    // pre-existing users from before this feature shipped.
    const dest = user?.default_home === 'profile' ? '/profile' : '/crate';
    return <Navigate to={dest} replace />;
  }
  return children;
}

function ThemedShell({ children }) {
  const { theme } = useTheme();
  // The actual `dark` class is added to <html> by ThemeContext, but we keep
  // it here too so any component that relies on the legacy wrapper class
  // (without :root selector) still picks up the right theme.
  return <div className={`app-shell ${theme === 'dark' ? 'dark' : ''}`}>{children}</div>;
}

function App() {
  useUnhandledRejectionGuard();
  return (
    <ThemeProvider>
      <ThemedShell>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <PublicOnlyRoute>
                    <Landing />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicOnlyRoute>
                    <Login />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicOnlyRoute>
                    <Signup />
                  </PublicOnlyRoute>
                }
              />
              <Route path="/oauth/success" element={<OAuthSuccess />} />

              {/* Shared links — accessible logged-out (renders a friendly
                  login wall itself) or logged-in (fetches the shared content). */}
              <Route path="/s/:token" element={<SharedView />} />

              {/* Promo — public, no auth. Renders an animated 60-second
                  commercial designed to be screen-recorded for TikTok / Reels
                  / Shorts. `?record=1` autoplays with all chrome hidden. */}
              <Route path="/promo" element={<Promo />} />

              <Route
                element={
                  <PrivateRoute>
                    <AppShell />
                  </PrivateRoute>
                }
              >
                <Route path="/crate" element={<Crate />} />
                <Route path="/upload" element={<Upload />} />
                <Route path="/upload/tracklist" element={<TracklistUpload />} />
                <Route path="/album/:bucket" element={<Album />} />
                <Route path="/owned" element={<Owned />} />
                <Route path="/unidentified" element={<Unidentified />} />
                <Route path="/review" element={<Review />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/edit" element={<ProfileEdit />} />
                <Route path="/stats" element={<Stats />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-center" />
        </AuthProvider>
      </ThemedShell>
    </ThemeProvider>
  );
}

export default App;
